import { AppDispatch, AppThunkAction, GetAppState } from '../index';
import * as types from '../constants';
import {
    ACTION_CHANGE_PRICE,
    ACTION_DECREASE,
    ACTION_INCREASE,
    ACTION_MOVE,
    ACTION_REMOVE,
    ACTION_SET,
    ListContentAction
} from '../constants';
import fileDownload from 'react-file-download';
import {
    globalErrorResponse,
    showNotification,
    showNotificationWithInfo,
    TYPE_ERROR,
    TYPE_WARNING
} from '../notifications/actions';
import {
    buildBulkRequestPathParamsAndData,
    deselectAll,
    determineGrouping,
    determineSourceParams,
    setSelectionMode
} from '../productSelection/actions';
import { withdrawnCallback } from '../orderlist/actions';
import { Price, SubscriptionPeriod, SubscriptionPeriodType } from '../../../models/Article';
import { UsageCondition } from '../../../models/Product';
import { cartConfig } from '../../config';
import { ListSourceType } from '../../../models/ListSourceType';
import { invalidateCartSummaryQuery } from '../../api/shopping-cart/useCartSummaryQuery';
import { EditListResponse } from '../api';
import { invalidateShoppingCartProducts } from '../../api/shopping-cart/useShoppingCartProducts';
import { invalidateCartGroupedItems } from '../../api/shopping-cart/useCartGroupedItems';
import { invalidateCartAggregations } from '../../shop/cart/useCartAggregations';
import { disableListLoading, enableListLoading } from '../listLoading/actions';
import i18n from '../../../i18n';
import Utils from '../../shared/utils';
import { http } from '../../api/axios';

const config = cartConfig();

export type UpdateProductOptions = {
    /**
     * The unique ID of a product, e.g. `"BOL_000001"`.
     */
    readonly id: string;

    /**
     * The human-readable title of the product identified by the given `id`.
     */
    readonly title?: string;

    readonly sourceUsageCondition?: UsageCondition;

    readonly price?: Price;

    readonly subscriptionPeriodType?: SubscriptionPeriodType;
    readonly subscriptionPeriod?: SubscriptionPeriod;

    readonly targetSubscriptionPeriodType?: SubscriptionPeriodType;
    readonly sourceSubscriptionPeriodType?: SubscriptionPeriodType;

    readonly source?: ListSourceType;
    readonly sourceId?: string;
};

export const removeProductFromCart = (product): AppThunkAction => {
    return (dispatch: AppDispatch) => {
        dispatch(
            updateProductInCart(
                { ...product, targetSubscriptionPeriodType: product.subscriptionPeriodType },
                types.ACTION_REMOVE,
                undefined,
                () => {
                    invalidateCartAggregations().then();
                    invalidateShoppingCartProducts().then();
                    invalidateCartGroupedItems().then();
                }
            )
        );
    };
};

function getMessageFromAction(action, productTitle): string | null {
    switch (action) {
        case types.ACTION_MOVE:
            return i18n.t('notifications.getMessageFromActionCart.move', { productTitle });
        case types.ACTION_COPY:
            return i18n.t('notifications.getMessageFromActionCart.copy', { productTitle });
        case types.ACTION_REMOVE:
            return i18n.t('notifications.getMessageFromActionCart.remove', { productTitle });
        case types.ACTION_ADD:
            return i18n.t('notifications.getMessageFromActionCart.add', { productTitle });
        default:
            return null;
    }
}

function updateProductInCart(
    options: UpdateProductOptions,
    action: ListContentAction,
    numCopies = 1,
    callback?: () => void,
    preOrderCheck: boolean = true,
    alreadyInListCheck: boolean = true
): AppThunkAction {
    const {
        id: productId,
        title: productTitle,
        price,
        sourceUsageCondition,
        source,
        sourceSubscriptionPeriodType,
        targetSubscriptionPeriodType
    } = options;
    const { lendingModel, usageLimit, termLimit } = price ?? {};
    const targetUsageCondition = { lendingModel, usageLimit, termLimit };

    return (dispatch: AppDispatch, state: GetAppState) => {
        const data = {
            action,
            amount: numCopies,
            ...determineSourceParams(state),
            ...determineGrouping(state),
            targetUsageCondition,
            sourceUsageCondition,
            sourceSubscriptionPeriodType,
            targetSubscriptionPeriodType
        };

        if (source) {
            data.source = source;
        }

        if (
            action === ACTION_MOVE ||
            action === ACTION_REMOVE ||
            action === ACTION_DECREASE ||
            action === ACTION_INCREASE ||
            action === ACTION_SET ||
            action === ACTION_CHANGE_PRICE
        ) {
            dispatch(enableListLoading());
        }

        const response = http.put<EditListResponse>(config.endpointProducts, data, {
            pathParams: { productId },
            params: { preOrderCheck, alreadyInListCheck }
        });
        response
            .then(() => {
                dispatch(showNotification(getMessageFromAction(action, productTitle)));
                invalidateCartSummaryQuery().then();
                if (callback) {
                    callback();
                }
            })
            .catch(error => dispatch(failAddToCart(error)))
            .finally(() => {
                dispatch(disableListLoading());
            });

        // Return the first promise so the consumers of this action can react depending on the success
        // of the action. If we return the promise returned from the `finally`, information about failure
        // is lost.
        return response;
    };
}

export function failAddToCart(error, source?): AppThunkAction {
    return (dispatch: AppDispatch, getState: GetAppState) => {
        if (!error.runtimeDuplication) {
            switch (error.response.status) {
                case 409: {
                    const data = error.response.data;
                    if (data.additionalInfo) {
                        const infoData = data.additionalInfo.map(p => Utils.withdrawnProductLine(p)).join('<br/>');
                        const isSelectedAllRequest = getState().productSelection.all;
                        const failMessage = isSelectedAllRequest
                            ? i18n.t('notifications.failAddToCart.fail')
                            : i18n.t('notifications.failAddToCart.proceed');
                        const showCancelButton = !isSelectedAllRequest;

                        dispatch(
                            showNotificationWithInfo(
                                data.message,
                                failMessage,
                                infoData,
                                data.additionalInfo,
                                showCancelButton ? products => withdrawnCallback(dispatch, source, products) : null,
                                showCancelButton
                            )
                        );
                        break;
                    }
                    dispatch(globalErrorResponse(error));
                    break;
                }
                default:
                    dispatch(globalErrorResponse(error));
            }
        }
        dispatch(deselectAll());
    };
}

export function removeFromCart(): AppThunkAction {
    return (dispatch: AppDispatch, state: GetAppState) => {
        const endpoint = config.endpointBulk;

        let requestdata;
        try {
            requestdata = buildBulkRequestPathParamsAndData(types.ACTION_REMOVE, 0, state);
        } catch (error) {
            dispatch(showNotification(i18n.t('notifications.removeFromCart.warning'), TYPE_WARNING));
            return;
        }

        const pathParams = requestdata.pathParams;
        const payload = requestdata.data;

        pathParams.source = types.SOURCE_QUANTITY;

        dispatch(enableListLoading());
        return http
            .put(endpoint, payload, { pathParams })
            .then(() => {
                dispatch(showNotification(i18n.t('notifications.removeFromCart.success')));
                invalidateCartSummaryQuery().then();
                invalidateCartGroupedItems().then();
                invalidateShoppingCartProducts().then();
                invalidateCartAggregations().then();
                dispatch(setSelectionMode(false));
                return Promise.resolve();
            })
            .catch(error => {
                dispatch(globalErrorResponse(error));
                dispatch(deselectAll());
                return Promise.reject();
            })
            .finally(() => {
                dispatch(disableListLoading());
            });
    };
}

export function downloadCSV(): AppThunkAction {
    return (dispatch: AppDispatch) => {
        http.get(config.endpointCSVDownload)
            .then(response => {
                fileDownload(response.data, 'cart.csv');
            })
            .catch(error => dispatch(globalErrorResponse(error)));
    };
}

export function deleteCart(): AppThunkAction {
    return (dispatch: AppDispatch) => {
        dispatch(enableListLoading());
        http.delete(config.endpointProducts)
            .then(() => {
                invalidateCartSummaryQuery().then();
                invalidateShoppingCartProducts().then();
                invalidateCartGroupedItems().then();
                invalidateCartAggregations().then();
            })
            .catch(error => dispatch(globalErrorResponse(error)))
            .finally(() => {
                dispatch(disableListLoading());
            });
    };
}

export const showOrdersNotAllowedNotification = () =>
    showNotification(i18n.t('notifications.showOrdersNotAllowedNotification.error'), TYPE_ERROR);
